/**
 * The 44px column at the end of a campaign row.
 *
 * Was two ghost icon buttons with no labels beside them. Pause and resume are
 * the one thing an operator does from the list rather than from the campaign, so
 * they stay one click away; deleting asks first, in a dialog.
 *
 * Delete only appears for a campaign that never sent anything. The ledger of
 * what was mailed to whom has to outlive its campaign, which is why the endpoint
 * refuses too - the menu just does not offer what it knows will fail.
 */
import { Copy, MoreHorizontal, Pause, Play, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { contactedTotal } from "./lib";
import type { CampaignRow } from "../../../lib/marketing";
import { Pop, useClosePops } from "../../shell/Pop";
import { useToast } from "../../ui";

export function CampaignRowMenu({
  row,
  canManage,
  busy,
  onToggle,
  onDelete,
}: {
  row: CampaignRow;
  canManage: boolean;
  busy: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const running = row.status === "active";

  async function copySlug() {
    close();
    try {
      await navigator.clipboard.writeText(row.slug);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
            disabled={busy}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button type="button" className="crm-mi" onClick={() => void copySlug()}>
          <Copy className="size-4" />
          {t("marketing.copySlug")}
        </button>

        {canManage ? (
          <>
            <div className="crm-menu-sep" />
            <button
              type="button"
              className="crm-mi"
              onClick={() => {
                close();
                onToggle();
              }}
            >
              {running ? <Pause className="size-4" /> : <Play className="size-4" />}
              {t(running ? "marketing.pause" : "marketing.activate")}
            </button>

            {/* ⚠ The SUM, not `row.sent`. Deleting a campaign cascades its whole
                send ledger away, so this item is hidden once anybody has been
                written to - and `sent` alone drops back towards zero as replies,
                bounces and opt-outs move rows off it. A campaign whose one
                letter got an answer would have offered Delete. The server
                refuses it either way (`contactedCount`), so this was a button
                that could only ever 409. */}
            {contactedTotal(row) === 0 ? (
              <>
                <div className="crm-menu-sep" />
                <button
                  type="button"
                  className="crm-mi crm-mi-danger"
                  onClick={() => {
                    close();
                    onDelete();
                  }}
                >
                  <Trash2 className="size-4" />
                  {t("common.delete")}
                </button>
              </>
            ) : null}
          </>
        ) : null}
      </Pop>
    </span>
  );
}
